import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Container,
  FilterButton,
  PresetButton,
  PresetGroup,
  Dropdown,
  DatePickerWrapper,
  ApplyButton,
  Backdrop,
} from './DateRangeFilter.styled';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

const DateRangeFilter = ({ startDate, endDate, onChange }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const containerRef = useRef(null);

  useClickOutside(containerRef, () => setIsOpen(false));

  const presets = [
    { label: t('last7Days'), days: 7 },
    { label: t('last30Days'), days: 30 },
    { label: t('last90Days'), days: 90 },
  ];

  const handlePresetClick = (days) => {
    const end = endOfDay(new Date());
    const start = startOfDay(subDays(end, days - 1));
    onChange(start, end);
    setIsOpen(false);
  };

  const handleApply = () => {
    if (tempStartDate && tempEndDate) {
      onChange(startOfDay(tempStartDate), endOfDay(tempEndDate));
      setIsOpen(false);
    }
  };

  const getButtonLabel = () => {
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 7) return t('last7Days');
    if (diffDays === 30) return t('last30Days');
    if (diffDays === 90) return t('last90Days');
    
    return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
  };

  return (
    <Container ref={containerRef}>
      <FilterButton onClick={() => setIsOpen(!isOpen)}>
        <i className="mdi mdi-calendar-range" />
        {getButtonLabel()}
        <i className={`mdi mdi-chevron-${isOpen ? 'up' : 'down'}`} />
      </FilterButton>
      
      {isOpen && (
        <>
          <Backdrop onClick={() => setIsOpen(false)} />
          <Dropdown>
          <PresetGroup>
            {presets.map((preset) => (
              <PresetButton
                key={preset.days}
                onClick={() => handlePresetClick(preset.days)}
              >
                {preset.label}
              </PresetButton>
            ))}
          </PresetGroup>
          
          <DatePickerWrapper>
            <div>
              <label>{t('startDate')}</label>
              <DatePicker
                selected={tempStartDate}
                onChange={(date) => setTempStartDate(date)}
                selectsStart
                startDate={tempStartDate}
                endDate={tempEndDate}
                maxDate={new Date()}
                inline
                locale={i18n.language}
              />
            </div>
            <div>
              <label>{t('endDate')}</label>
              <DatePicker
                selected={tempEndDate}
                onChange={(date) => setTempEndDate(date)}
                selectsEnd
                startDate={tempStartDate}
                endDate={tempEndDate}
                minDate={tempStartDate}
                maxDate={new Date()}
                inline
                locale={i18n.language}
              />
            </div>
          </DatePickerWrapper>
          
          <ApplyButton onClick={handleApply}>{t('applyPeriod')}</ApplyButton>
        </Dropdown>
        </>
      )}
    </Container>
  );
};

DateRangeFilter.propTypes = {
  startDate: PropTypes.instanceOf(Date).isRequired,
  endDate: PropTypes.instanceOf(Date).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default DateRangeFilter;

